<template>
  <div 
    ref="triggerRef" 
    class="popover-trigger"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <slot name="reference"></slot>
    
    <Teleport to="body">
      <Transition name="popover-fade">
        <div
          v-if="visible"
          ref="popoverRef"
          class="popover"
          :class="[`popover--${placement}`, popoverClass]"
          :style="{
            ...popoverStyle,
            width: props.width ? `${props.width}px` : 'auto',
          }"
          @mouseenter="handlePopoverMouseEnter"
          @mouseleave="handlePopoverMouseLeave"
        >
          <div class="popover__arrow"></div>
          <div class="popover__content">
            <slot></slot>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  placement: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value)
  },
  trigger: {
    type: String,
    default: 'hover',
    validator: (value) => ['hover', 'click'].includes(value)
  },
  popoverClass: {
    type: String,
    default: ''
  },
  offset: {
    type: Number,
    default: 8
  },
  disabled: {
    type: Boolean,
    default: false
  },
  width: {
    type: Number,
    default: 0
  },
});

const emit = defineEmits(['show', 'hide']);

const triggerRef = ref(null);
const popoverRef = ref(null);
const visible = ref(false);
const popoverStyle = ref({});

let hideTimer = null;

const handleMouseEnter = () => {
  if (props.disabled || props.trigger !== 'hover') return;
  clearTimeout(hideTimer);
  showPopover();
};

const handleMouseLeave = () => {
  if (props.disabled || props.trigger !== 'hover') return;
  hideTimer = setTimeout(() => {
    hidePopover();
  }, 200);
};

const handlePopoverMouseEnter = () => {
  if (props.trigger !== 'hover') return;
  clearTimeout(hideTimer);
};

const handlePopoverMouseLeave = () => {
  if (props.trigger !== 'hover') return;
  hideTimer = setTimeout(() => {
    hidePopover();
  }, 200);
};

const handleClick = () => {
  if (props.disabled || props.trigger !== 'click') return;
  if (visible.value) {
    hidePopover();
  } else {
    showPopover();
  }
};

const showPopover = async () => {
  visible.value = true;
  emit('show');
  await nextTick();
  updatePosition();
};

const hidePopover = () => {
  visible.value = false;
  emit('hide');
};

const updatePosition = async () => {
  if (!triggerRef.value || !popoverRef.value) return;
  const triggerRect = triggerRef.value.getBoundingClientRect();
  const popoverRect = popoverRef.value.getBoundingClientRect();

  const triggerWidth = triggerRect.width

  const popoverWidth = props.width || popoverRect.width

  const { offset } = props;
  
  let top = 0;
  let left = 0;
  
  switch (props.placement) {
    case 'top':
      top = triggerRect.top - popoverRect.height - offset;
      left = triggerRect.left + (triggerWidth  - popoverWidth) / 2;
      break;
    case 'bottom':
      top = triggerRect.bottom + offset;
      left = triggerRect.left + (triggerWidth - popoverWidth) / 2;
      break;
    case 'left':
      top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
      left = triggerRect.left - popoverWidth - offset;
      break;
    case 'right':
      top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
      left = triggerRect.right + offset;
      break;
  }

  // 边界检测和调整
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  if (left < 0) left = 8;
  if (left + popoverRect.width > viewportWidth) {
    left = viewportWidth - popoverRect.width - 8;
  }
  if (top < 0) top = 8;
  if (top + popoverRect.height > viewportHeight) {
    top = viewportHeight - popoverRect.height - 8;
  }

  popoverStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 10000
  };
};

// 点击外部关闭
const handleClickOutside = (event) => {
  if (props.trigger !== 'click' || !visible.value) return;
  
  if (
    triggerRef.value && 
    popoverRef.value &&
    !triggerRef.value.contains(event.target) &&
    !popoverRef.value.contains(event.target)
  ) {
    hidePopover();
  }
};

// 滚动时关闭悬浮窗
const handleScroll = () => {
  if (visible.value) {
    hidePopover();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', handleScroll);
  clearTimeout(hideTimer);
});
</script>

<style lang='less' scoped>
.popover-trigger {
  display: inline-block;
}

.popover {
  position: fixed;
  background: #fff;
  border: 1px solid #dfdfdf;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
  padding: 0;
  font-size: 14px;
  line-height: 1.4;
  min-width: 10px;
  word-wrap: break-word;

  &__content {
    padding: 16px;
  }

  &__arrow {
    position: absolute;
    width: 0;
    height: 0;
  }

  // 箭头位置
  &--top {
    .popover__arrow {
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      border-top-color: #e4e7ed;
      border-bottom: none;
      
      &::after {
        content: '';
        position: absolute;
        top: -7px;
        left: -5px;
        border: 8px solid transparent;
        border-top-color: #fff;
        border-bottom: none;
      }
    }
  }

  &--bottom {
    .popover__arrow {
      top: -6px;
      left: 50%;
      transform: translateX(-50%);
      border-bottom-color: #e4e7ed;
      border-top: none;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -7px;
        left: -5px;
        border: 8px solid transparent;
        border-bottom-color: #fff;
        border-top: none;
      }
    }
  }

  &--left {
    .popover__arrow {
      right: -6px;
      top: 50%;
      transform: translateY(-50%);
      border-left-color: #e4e7ed;
      border-right: none;
      
      &::after {
        content: '';
        position: absolute;
        left: -7px;
        top: -5px;
        border: 8px solid transparent;
        border-left-color: #fff;
        border-right: none;
      }
    }
  }

  &--right {
    .popover__arrow {
      left: -6px;
      top: 50%;
      transform: translateY(-50%);
      border-right-color: #e4e7ed;
      border-left: none;
      
      &::after {
        content: '';
        position: absolute;
        right: -7px;
        top: -5px;
        border: 8px solid transparent;
        border-right-color: #fff;
        border-left: none;
      }
    }
  }
}

// 过渡动画
.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1), transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.popover-fade-enter-from,
.popover-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>